import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { messagingService } from '../../../lib/services';

const NewMessageModal = ({ onClose, onCreateThread }) => {
  const [subject, setSubject] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const { data, error: fetchError } = await messagingService?.getAllUsers();
    if (!fetchError && data) {
      setUsers(data);
    }
    setLoading(false);
  };

  const filteredUsers = users?.filter(user => {
    const query = searchQuery?.toLowerCase();
    return (
      user?.full_name?.toLowerCase()?.includes(query) ||
      user?.email?.toLowerCase()?.includes(query) ||
      user?.role?.toLowerCase()?.includes(query)
    );
  });

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev?.includes(userId)) {
        return prev?.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleCreate = async () => {
    if (!subject?.trim() || selectedUsers?.length === 0) {
      setError('Please enter a subject and select at least one recipient');
      return;
    }

    setCreating(true);
    setError(null);
    const { error: createError } = await onCreateThread(subject, selectedUsers);
    if (createError) {
      setError(createError?.message || 'Failed to create conversation');
      setCreating(false);
    } else {
      onClose();
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      member: 'bg-blue-500/10 text-blue-600',
      trainer: 'bg-purple-500/10 text-purple-600',
      staff: 'bg-green-500/10 text-green-600',
      manager: 'bg-orange-500/10 text-orange-600',
      owner: 'bg-red-500/10 text-red-600'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">New Message</h2>
            <p className="text-sm text-muted-foreground mt-1">Start a conversation with trainers, staff, or members</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {error && (
            <div className="bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <Input
            label="Subject"
            type="text"
            placeholder="What is this conversation about?"
            value={subject}
            onChange={(e) => setSubject(e?.target?.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Recipients ({selectedUsers?.length} selected)
            </label>
            <Input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="mb-4"
            />

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto">
                {filteredUsers?.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  filteredUsers?.map((user) => {
                    const isSelected = selectedUsers?.includes(user?.id);
                    return (
                      <button
                        key={user?.id}
                        onClick={() => handleUserToggle(user?.id)}
                        className={`
                          w-full p-4 text-left transition-colors hover:bg-muted
                          ${isSelected ? 'bg-primary/5' : ''}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected ? 'border-primary bg-primary' : 'border-border'}
                          `}>
                            {isSelected && <Icon name="Check" size={14} color="white" />}
                          </div>
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={20} color="var(--color-primary)" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-foreground text-sm truncate">
                                {user?.full_name}
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user?.role)}`}>
                                {user?.role}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !subject?.trim() || selectedUsers?.length === 0}
            iconName="Send"
            iconPosition="right"
          >
            {creating ? 'Creating...' : 'Start Conversation'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewMessageModal;
